import { FilterMetadata, SelectItem, SortMeta } from 'primeng/api';
import {
  AmortizationType,
  ArmFixedTerm,
  ArmSubsequentChangePeriod,
  AutomatedUWSystem,
  BankruptcyOutcome,
  BankruptcySeasoning,
  BankruptcyType,
  BestPricing,
  BuyDown as BuyDown,
  Citizenship,
  CoveragePercentage,
  CoverageType,
  DesiredLockPeriod,
  DesiredType,
  EscrowWaiver,
  FhaCaseAssigned,
  HousingEventSeasoning,
  HousingEventType,
  HousingExpense,
  HousingExpenseFormValue,
  IncomeVerificationType,
  LoanPurpose,
  LoanTerm,
  MiMethod,
  MIProvider,
  MIProviderWithoutBestEx,
  MortgageType,
  NumberOfUnits,
  OccupancyType,
  PrepaymentPenalty,
  PrepaymentStructure,
  PricingBorrower,
  PricingResult,
  PricingRunData,
  ProductType,
  PropertyType,
  SecondMortgage,
  SectionOfAct,
  RerunPricingTitleInfo,
  UsdaCaseAssigned,
  VaFundingFeeType,
} from './pricing.models';
import { ScenarioFieldType } from './scenarios.models';
import { FormControl } from '@angular/forms';
import { PricingInputFormModel } from './pricing-input-form.model';

export interface ScenarioRow {
  scenarioGUID?: string | null;
  scenarioCriteriaGUID?: string | null;
  scenarioName?: string | null;
  secondMortgage?: SecondMortgage;
  amortizationType?: AmortizationType;
  borrowerLastName?: string | null;
  borrowerFirstName?: string | null;
  scenarioArmFixedTerm?: ArmFixedTerm;
  scenarioCriteriaArmFixedTerms?: ArmFixedTerm[] | null;
  automatedUnderwritingSystem?: AutomatedUWSystem;
  baseLoanAmount?: number | null;
  baseLtv?: number | null;
  borrowerPaysMi?: boolean | null;
  borrowerPaysMiConv?: boolean | null;
  borrowerPaysMiGovt?: boolean | null;
  buyDown?: BuyDown;
  cashOutAmount?: number | null;
  citizenship?: Citizenship;
  city?: string | null;
  communityLending?: boolean | null;
  communityAffordableSecond?: boolean | null;
  county?: string | null;
  desiredLockPeriod?: number | null;
  desiredPrice?: number | null;
  desiredRate?: number | null;
  discountRebatePercent?: number | null;
  discountRebateAmount?: number | null;
  downPaymentPercent?: number | null;
  downPaymentAmount?: number | null;
  dti?: number | null;
  purchasePrice?: number; // UI ONLY
  estimatedValue?: number; // UI ONLY
  exemptFromFundingFee?: boolean | null;
  fhaCaseAssigned?: FhaCaseAssigned;
  fico?: number | null;
  isFinanceEntireAmount?: boolean | null;
  isFinanceEntireAmountConv?: boolean | null;
  isFinanceEntireAmountGovt?: boolean | null;
  firstTimeHomeBuyer?: boolean | null;
  heLoanAmount?: number | null;
  helocLineAmount?: number | null;
  helocDrawnAmount?: number | null;
  interestOnly?: boolean | null;
  isPud?: boolean | null;
  interestRate?: number | null;
  lastPriced?: Date | null;
  loanPurpose?: LoanPurpose;
  loanTerm: LoanTerm;
  scenarioLoanTerm?: LoanTerm;
  scenarioCriteriaLoanTerms?: LoanTerm[] | null;
  lockPeriod?: number | null;
  mhAdvantage?: boolean | null;
  monthsOfReserve?: number | null;
  monthlyPI?: number | null;
  monthlyPITI?: number | null;
  monthlyQualifyingIncome?: number | null;
  scenarioMortgageType?: MortgageType;
  scenarioCriteriaMortgageTypes?: MortgageType[] | null;
  mortgageType?: MortgageType;
  noOfFinancedProperties?: number | null;
  noOfStories?: number | null;
  noOfUnits?: NumberOfUnits;
  nonOccupantCoBorrower?: boolean | null;
  occupancyType?: OccupancyType;
  upfrontPmiMipFfGFeePercent?: number | null;
  upfrontPmiMipFfGFeePercentConv?: number | null;
  upfrontPmiMipFfGFeePercentGovt?: number | null;
  upfrontPmiMipFfGFeeAmount?: number | null;
  upfrontPmiMipFfGFeeAmountConv?: number | null;
  upfrontPmiMipFfGFeeAmountGovt?: number | null;
  upfrontPmiMipFfGFeeAmountFinanced?: number | null;
  upfrontPmiMipFfGFeeAmountFinancedConv?: number | null;
  upfrontPmiMipFfGFeeAmountFinancedGovt?: number | null;
  upfrontPmiMipFfGFeeAmountCash?: number | null;
  upfrontPmiMipFfGFeeAmountCashConv?: number | null;
  upfrontPmiMipFfGFeeAmountCashGovt?: number | null;
  upfrontPremiumAmount?: number | null;
  upfrontPremiumRate?: number | null;
  monthlyPmiMipFfGFeePercent?: number | null;
  monthlyPmiMipFfGFeeAmount?: number | null;
  price?: number | null;
  productID?: number | null;
  productName?: string | null;
  propertyType?: PropertyType;
  lockExpirationDate?: Date | null;
  state?: string | null;
  totalLoanAmount?: number | null;
  totalLoanAmountConv?: number | null;
  totalLoanAmountGovt?: number | null;
  usdaCaseAssigned?: UsdaCaseAssigned;
  vaFundingFeeType?: VaFundingFeeType;
  waiveEscrows?: boolean | null;
  zip?: string | null;
  clear1ScenarioPricingsId?: number | null;
  productType?: ProductType;
  timesMortPmntLateBy30DaysInLast1To12Mos?: number;
  timesMortPmntLateBy60DaysInLast1To12Mos?: number;
  timesMortPmntLateBy90DaysInLast1To12Mos?: number;
  timesMortPmntLateBy120DaysInLast1To12Mos?: number;
  timesMortPmntLateBy30DaysInLast13To24Mos?: number;
  timesMortPmntLateBy60DaysInLast13To24Mos?: number;
  timesMortPmntLateBy90DaysInLast13To24Mos?: number;
  timesMortPmntLateBy120DaysInLast13To24Mos?: number;
  selfEmployed?: boolean;
  incomeVerificationType?: IncomeVerificationType;
  dscr?: number;
  bankruptcyType?: BankruptcyType;
  bankruptcyOutcome?: BankruptcyOutcome;
  bankruptcySeasoning?: BankruptcySeasoning;
  housingEventType?: HousingEventType;
  housingEventSeasoning?: HousingEventSeasoning;
  scenarioCriteriaProductTypes?: ProductType[];
  expandedGuidelines?: boolean;
  prepaymentPenalty?: PrepaymentPenalty;
  street?: string | null;
  armFirstCap?: number | null;
  armPeriodicCap?: number | null;
  armRelativeCap?: number | null;
  criteriaMiProviders?: number[] | MIProvider[];
  coverageType?: number;
  coveragePercentage?: number;
  htiWithoutMi?: number;
  dtiWithoutMi?: number;
  borrowerFico?: number;
  borrowerSelfEmployed?: boolean;
  borrowerHadForeclosure?: boolean;
  borrowerHadBankruptcy?: boolean;
  coBorrowerFico?: number;
  coBorrowerSelfEmployed?: boolean;
  coBorrowerHadForeclosure?: boolean;
  coBorrowerHadBankruptcy?: boolean;
  bestXMi?: boolean;
  pmiType?: PmiType | number;
  monthlyPremiumAmount?: number;
  monthlyPremiumRate?: number;
  miProvider?: number;
  coveragePercent?: number | null; // This is BestX MI coveragePercentage
  armSubsequentChangePeriod?: ArmSubsequentChangePeriod | number | string;
  debtConsolidation?: boolean;
  uniqueProperty?: boolean;
  prepaymentStructure?: PrepaymentStructure | number;
  desiredType?: DesiredType;
  estimatedClosingDate?: string;
  housingExpenses?: HousingExpense[];
  //Card View UI only fields
  borrowerFullNameDisplay?: string;
  cityStateZipDisplay?: string;
  lockPeriodExpirationDisplay?: string;
  amortizationTermDisplay?: string;
  monthlyPiPitiDisplay?: string;
  discountRebateDisplay?: string;
  downPaymentDisplay?: string;
  scenarioCriteriaMortgageTypesDisplay?: string;
  scenarioCriteriaAmortizationTermsDisplay?: string;
  scenarioCriteriaLoanTermsDisplay?: string;
  scenarioCriteriaProductTypesDisplay?: string;
  mortgageLateHistoryDisplay?: MortgageLateHistoryRow[];
  proposedMonthlyPaymentBreakdown?: ProposedMonthlyPaymentBreakdown;
  monthlyPmiMipFfGFeeDisplay?: string;
  upfrontPmiMipFfGFeeDisplay?: string;
  monthlyPremiumDisplay?: string;
  upfrontPremiumDisplay?: string;
  attributesDisplay?: AttributeRow[];
  criteriaMiProviderDisplay?: string;
  scenarioNameEditMode?: boolean;
  scenarioNameCtrl?: FormControl<string>;
  scenarioCriteriaMiMethod?: MiMethod;
  fhaPrevEndorsementDate?: string;
  fhaSectionOfAct?: SectionOfAct | number;
  cardsAndOtherDebtPaidOff?: number;
  earnestMoneyDeposit?: number;
  mortgageLoansToBePaid?: number;
  lumpSumSellerCredit?: number;
}

export type Numberish = number | null | undefined;

export type KeysOfType<T, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

export type NumberishKeyOfScenarioRow = KeysOfType<ScenarioRow, Numberish>;

export interface ColumnConfig {
  colId?: keyof ScenarioRow;
  headerName?: string;
}

export interface HousingExpenseBreakdown {
  supplementalPropertyTaxTotal?: number;
  propertyTaxTotal?: number;
  otherTotal?: number;
  homeownersInsurance?: number;
  hoaDues?: number;
}

export interface ProposedMonthlyPaymentBreakdown {
  monthlyPI?: number | null;
  homeownersInsurance?: number | null;
  supplementalPropertyInsurance?: number | null;
  propertyTaxes?: number | null;
  mortgageInsurance?: number | null;
  hoaDues?: number | null;
  other?: number | null;
  totalPiti?: number | null;
}

export enum AttributeBucket {
  FICO = 'FICO',
  SelfEmployed = 'Self Employed',
  Foreclosure7Yrs = 'Foreclosure (7 Yrs)',
  Bankruptcy7Yrs = 'Bankruptcy (7 Yrs)',
}

export enum BorrowerAttributeField {
  FICO = 'borrowerFico',
  SelfEmployed = 'borrowerSelfEmployed',
  Foreclosure7Yrs = 'borrowerHadForeclosure',
  Bankruptcy7Yrs = 'borrowerHadBankruptcy',
}

export enum CoBorrowerAttributeField {
  FICO = 'coBorrowerFico',
  SelfEmployed = 'coBorrowerSelfEmployed',
  Foreclosure7Yrs = 'coBorrowerHadForeclosure',
  Bankruptcy7Yrs = 'coBorrowerHadBankruptcy',
}

export interface AttributeRow {
  bucket?: AttributeBucket;
  borrowerAttribute?: number | boolean;
  coBorrowerAttribute?: number | boolean;
  type?: 'checkbox' | 'number';
}

export const borrowerAttributeFieldByBucket: Record<AttributeBucket, BorrowerAttributeField> = {
  [AttributeBucket.FICO]: BorrowerAttributeField.FICO,
  [AttributeBucket.SelfEmployed]: BorrowerAttributeField.SelfEmployed,
  [AttributeBucket.Foreclosure7Yrs]: BorrowerAttributeField.Foreclosure7Yrs,
  [AttributeBucket.Bankruptcy7Yrs]: BorrowerAttributeField.Bankruptcy7Yrs,
};

export const coBorrowerAttributeFieldByBucket: Record<AttributeBucket, CoBorrowerAttributeField> = {
  [AttributeBucket.FICO]: CoBorrowerAttributeField.FICO,
  [AttributeBucket.SelfEmployed]: CoBorrowerAttributeField.SelfEmployed,
  [AttributeBucket.Foreclosure7Yrs]: CoBorrowerAttributeField.Foreclosure7Yrs,
  [AttributeBucket.Bankruptcy7Yrs]: CoBorrowerAttributeField.Bankruptcy7Yrs,
};

export enum MortgageLateBucket {
  Days30 = '30 Days',
  Days60 = '60 Days',
  Days90 = '90 Days',
  Days120 = '120 Days',
}

export enum MortgageLateFieldLast12Months {
  Days30 = 'timesMortPmntLateBy30DaysInLast1To12Mos',
  Days60 = 'timesMortPmntLateBy60DaysInLast1To12Mos',
  Days90 = 'timesMortPmntLateBy90DaysInLast1To12Mos',
  Days120 = 'timesMortPmntLateBy120DaysInLast1To12Mos',
}

export enum MortgageLateFieldLast13To24Months {
  Days30 = 'timesMortPmntLateBy30DaysInLast13To24Mos',
  Days60 = 'timesMortPmntLateBy60DaysInLast13To24Mos',
  Days90 = 'timesMortPmntLateBy90DaysInLast13To24Mos',
  Days120 = 'timesMortPmntLateBy120DaysInLast13To24Mos',
}

export interface MortgageLateHistoryRow {
  bucket?: MortgageLateBucket;
  last1To12Months?: number | string;
  last13To24Months?: number | string;
}

export const mortgageLateFieldLast12MonthsByBucket: Record<MortgageLateBucket, MortgageLateFieldLast12Months> = {
  [MortgageLateBucket.Days30]: MortgageLateFieldLast12Months.Days30,
  [MortgageLateBucket.Days60]: MortgageLateFieldLast12Months.Days60,
  [MortgageLateBucket.Days90]: MortgageLateFieldLast12Months.Days90,
  [MortgageLateBucket.Days120]: MortgageLateFieldLast12Months.Days120,
};

export const mortgageLateFieldLast13To24ByBucket: Record<MortgageLateBucket, MortgageLateFieldLast13To24Months> = {
  [MortgageLateBucket.Days30]: MortgageLateFieldLast13To24Months.Days30,
  [MortgageLateBucket.Days60]: MortgageLateFieldLast13To24Months.Days60,
  [MortgageLateBucket.Days90]: MortgageLateFieldLast13To24Months.Days90,
  [MortgageLateBucket.Days120]: MortgageLateFieldLast13To24Months.Days120,
};

export interface ScenarioSortMeta extends SortMeta {
  /**
   * If sortMeta includes multisortMeta -> GroupBy is included
   * In case of GroupBy, ScenarioSortMeta will have multiSortMeta in place sortMeta
   */
  multisortmeta?: SortMeta[];
}

// interface used for DTO passed in "Run Pricing" call
export interface ScenarioCriteriaModel {
  guid?: string;
  scenarioCriteriaGUID?: string;
  scenarioLinkageGUID?: string;
  noOfScenarios?: number;
  scenarioName?: string;
  userGUID?: string;
  userOrgProfileID?: number;
  channel?: number;
  fileDataId?: number;
  loanId?: number;
  baseLoanAmount?: number;
  street?: string;
  city?: string;
  state: string;
  county: string;
  zip: string;
  heLoanAmount?: number;
  helocLineAmount?: number;
  helocDrawnAmount?: number;
  searchFirstLien?: boolean;
  purchasePrice?: number;
  estimatedValue?: number;
  appraisedValue?: number;
  loanPurpose?: LoanPurpose | string | number;
  cashOutAmount?: number;
  baseLtv?: number;
  ltv?: number;
  cltv?: number;
  hcltv?: number;
  waiveEscrows?: boolean;
  borrowerFirstName?: string;
  borrowerLastName?: string;
  fico?: number;
  citizenship?: Citizenship | string;
  automatedUnderwritingSystem?: AutomatedUWSystem | string;
  firstTimeHomeBuyer?: boolean;
  secondMortgage?: number | string;
  nonOccupantCoBorrower?: boolean;
  occupancyType: OccupancyType | string;
  propertyType: PropertyType | string;
  noOfUnits: NumberOfUnits | string;
  noOfStories: number;
  mortgageTypes?: MortgageType[] | string[];
  amortizationTypes?: AmortizationType[] | string[];
  loanTerms?: LoanTerm[] | string[] | number[];
  armFixedTerms?: ArmFixedTerm[] | string[];
  armSubsequentChangePeriod?: ArmSubsequentChangePeriod | number | string;
  desiredType?: DesiredType;
  desiredPrice?: number;
  desiredRate?: number;
  desiredLockPeriod?: number;
  buyDown?: BuyDown | string;
  borrowerPaysMiConv?: boolean;
  borrowerPaysMiGovt?: boolean;
  fhaCaseAssigned?: FhaCaseAssigned | string;
  fhaCaseAssignedDate?: string;
  vaFundingFeeType?: VaFundingFeeType | string;
  exemptFromFundingFee?: boolean;
  usdaCaseAssigned?: UsdaCaseAssigned;
  monthsOfReserve?: number;
  upfrontPmiMipFfGFeePercentConv?: number;
  upfrontPmiMipFfGFeeAmountConv?: number;
  upfrontPmiMipFfGFeePercentGovt?: number;
  upfrontPmiMipFfGFeeAmountGovt?: number;
  upfrontPmiMipFfGFeeAmountFinancedConv?: number;
  upfrontPmiMipFfGFeeAmountCashConv?: number;
  upfrontPmiMipFfGFeeAmountFinancedGovt?: number;
  upfrontPmiMipFfGFeeAmountCashGovt?: number;
  monthlyPmiMipFfGFeePercentConv?: number;
  monthlyPmiMipFfGFeeAmountConv?: number;
  monthlyPmiMipFfGFeePercentGovt?: number;
  monthlyPmiMipFfGFeeAmountGovt?: number;
  totalLoanAmountConv?: number;
  totalLoanAmountGovt?: number;
  downPaymentPercent?: number;
  downPaymentAmount?: number;
  isFinanceEntireAmountConv?: boolean;
  isFinanceEntireAmountGovt?: boolean;
  interestOnly?: boolean;
  dti: number;
  monthlyQualifyingIncome?: number;
  corporateRelocation?: boolean;
  calculateTotalLoanAmount?: boolean;
  global?: string;
  pmiType?: PmiType | string;
  miMethod?: MiMethod | number;
  scenarioNameAiSuffix?: string;
  /* Product Type(s) */
  productTypes: ProductType[] | string[];
  timesMortPmntLateBy30DaysInLast1To12Mos?: number;
  timesMortPmntLateBy60DaysInLast1To12Mos?: number;
  timesMortPmntLateBy90DaysInLast1To12Mos?: number;
  timesMortPmntLateBy120DaysInLast1To12Mos?: number;
  timesMortPmntLateBy30DaysInLast13To24Mos?: number;
  timesMortPmntLateBy60DaysInLast13To24Mos?: number;
  timesMortPmntLateBy90DaysInLast13To24Mos?: number;
  timesMortPmntLateBy120DaysInLast13To24Mos?: number;
  incomeVerificationType?: IncomeVerificationType | string;
  dscr?: number | string;
  prepaymentPenalty?: PrepaymentPenalty | string;
  selfEmployed: boolean;
  bankruptcyType?: BankruptcyType | string;
  bankruptcyOutcome?: BankruptcyOutcome | string;
  bankruptcySeasoning?: BankruptcySeasoning | string;
  housingEventType?: HousingEventType | string;
  housingEventSeasoning?: HousingEventSeasoning | string;
  communityLending?: boolean;
  mhAdvantage?: boolean;
  communityAffordableSecond?: boolean;
  noOfFinancedProperties?: number;
  miProviders: MIProvider[];
  htiWithoutMi: number;
  dtiWithoutMi: number;
  addCoBorrower: boolean;
  borrowers?: PricingBorrower[];
  coverageType: CoverageType | number;
  coveragePercentage: CoveragePercentage | number;
  /* end Product Type(s) */
  /* Housing Expenses */
  // housingExpenses is the only property sent in the Save Scenario API payload.
  // The grouped form properties below are mapped to build housingExpenses before save.
  housingExpenses?: HousingExpense[];
  propertyTax?: HousingExpenseFormValue;
  homeownersInsurance?: HousingExpenseFormValue;
  floodInsurance?: HousingExpenseFormValue;
  hoaDues?: HousingExpenseFormValue;
  additionalHousingExpenses?: HousingExpenseFormValue[];
  uniqueProperty?: boolean;
  debtConsolidation?: boolean;
  prepaymentStructure?: PrepaymentStructure | number;
  estimatedClosingDate?: string;
  estimatedFundingDate?: string;
  firstPaymentDate?: string;
  aggregateAdjustment?: number;
  escrowWaiver?: EscrowWaiver;
  fhaSectionOfAct?: SectionOfAct | number;
  fhaPrevEndorsementDate?: string;
  isPud?: boolean;
  cardsAndOtherDebtPaidOff?: number;
  earnestMoneyDeposit?: number;
  mortgageLoansToBePaid?: number;
  lumpSumSellerCredit?: number;
}

export interface RunOriginatorAssistantRequest {
  criteria?: ScenarioCriteriaModel;
  product?: TargetProduct;
  searchResultInformation?: SearchResultInformation;
}

export interface TargetProduct {
  interestRate?: number;
  loanTerm?: LoanTerm;
  lockPeriod?: DesiredLockPeriod;
  mortgageType?: MortgageType;
  price?: number;
}

export interface SearchResultInformation {
  ltv?: number;
  cltv?: number;
  hcltv?: number;
}

export interface RunOriginatorAssistantResponse {
  priceSuggestionId?: string;
  lockPeriod?: DesiredLockPeriod;
  suggestions?: Suggestion[];
}

export interface Suggestion {
  suggestionText?: string;
  rate?: number;
  principalAndInterest?: number;
  discountRebateAmount?: number;
  discountRebatePercent?: number;
  price?: number;
  baseLoanAmount?: number;
  representativeFico?: number;
  dti?: number;
  interestRate?: number; // UI use only
}

export enum SuggestionField {
  BaseLoanAmount = 'baseLoanAmount',
  Fico = 'representativeFico',
  Dti = 'dti',
}

export const aiSuggestLabels: Record<SuggestionField, string> = {
  [SuggestionField.BaseLoanAmount]: 'Run AI: Base Loan Amount',
  [SuggestionField.Fico]: 'Run AI: FICO',
  [SuggestionField.Dti]: 'Run AI: DTI',
};

export const suggestionFieldToFormKey: Record<SuggestionField, string> = {
  [SuggestionField.BaseLoanAmount]: 'baseLoanAmount',
  [SuggestionField.Fico]: 'fico',
  [SuggestionField.Dti]: 'dti',
};

export interface PipelineScenarioResponse {
  cardViewLayout?: ScenarioLayout;
  listViewLayout?: ScenarioLayout;
  offsetN?: number;
  pipelineScenarios?: ScenarioRow[];
  scenarioLayout?: ScenarioLayout;
  topN?: number;
  totalResults?: number;
}

export interface ScenarioLayoutResponse {
  guid: string;
}

export interface ErrorDetails {
  detail: string;
  emailSubject: string;
  emailBody: string;
}

export enum HttpErrorSource {
  GetRateSheet = 1,
  GetIneligibleProducts = 2,
  SaveScenarios = 3,
  SaveAndReplaceScenarios = 4,
  RunBestXMiRequest = 5,
  ProductGroupAndDetails = 6,
  ScenarioOptimizer = 7,
}

export enum MiProvider {
  None = 0,
  Arch = 1,
  Enact = 2,
  Essent = 4,
  Mgic = 8,
  National = 16,
  Radian = 32,
  All = 64,
}

export enum PipelineScenarioFilterOperator {
  InList = 0,
  Equals = 1,
  NotEquals = 2,
  LessThan = 3,
  LessThanOrEqual = 4,
  GreaterThan = 5,
  GreaterThanOrEqual = 6,
  Between = 7,
  Blank = 8,
  NotBlank = 9,
  StartsWith = 10,
  EndsWith = 11,
  Contains = 12,
  NotContains = 13,
  DateIs = 14,
  DateIsNot = 15,
  DateBefore = 16,
  DateOnOrBefore = 17,
  DateAfter = 18,
  DateOnOrAfter = 19,
}

export enum ColumnInfoLabel {
  armFirstCap = 'ARM First Rate Adjustment Cap',
  armPeriodicCap = 'ARM Subsequent Rate Adjustment Cap',
  armRelativeCap = 'ARM Life Rate Adjustment Cap',
  interestRate = 'Interest Rate',
  MonthlyPITI = 'Monthly PITI',
}

export enum PmiType {
  BorrowerMonthlyNonRefundableConstant = 1,
  SplitConstantUpfront100 = 2,
  BorrowerSingleNonRefundable = 3,
  LenderSingleNonRefundable = 4,
}

export const pmiTypeMap: Record<PmiType, string> = {
  [PmiType.BorrowerMonthlyNonRefundableConstant]: 'Monthly',
  [PmiType.SplitConstantUpfront100]: 'Split',
  [PmiType.BorrowerSingleNonRefundable]: 'Upfront',
  [PmiType.LenderSingleNonRefundable]: 'LPMI',
};

export const pmiTypeOptions: SelectItem<PmiType>[] = [
  { label: 'Monthly', value: PmiType.BorrowerMonthlyNonRefundableConstant },
  { label: 'Split', value: PmiType.SplitConstantUpfront100 },
  { label: 'Upfront', value: PmiType.BorrowerSingleNonRefundable },
  // Commenting out for #72392
  // { label: 'LPMI', value: PmiType.LenderSingleNonRefundable },
];

type ColumnNames = { name: string; rateName: string };

export const pmiColumnMap: Record<PmiType, ColumnNames> = {
  [PmiType.BorrowerMonthlyNonRefundableConstant]: {
    name: 'Monthly Premium',
    rateName: 'Monthly Premium Rate',
  },
  [PmiType.SplitConstantUpfront100]: {
    name: 'Monthly Premium',
    rateName: 'Monthly Premium Rate',
  },
  [PmiType.BorrowerSingleNonRefundable]: {
    name: 'Upfront Premium',
    rateName: 'Upfront Rate',
  },
  [PmiType.LenderSingleNonRefundable]: {
    name: 'Upfront Premium',
    rateName: 'Upfront Rate',
  },
};

export enum ErrorCode {
  UserSetupIssue = 'USER_SETUP_ISSUE',
  VendorSetupIssue = 'VENDOR_SETUP_ISSUE',
  OBError = 'OB_ERROR',
}

export const errorTemplates: Partial<
  Record<
    HttpErrorSource | 'default',
    {
      vendor: ErrorDetails;
      standard: ErrorDetails;
    }
  >
> = {
  [HttpErrorSource.RunBestXMiRequest]: {
    vendor: {
      detail: 'Some information may be missing. Re-run MI or contact support to resolve the issue.',
      emailSubject: 'Scenario Pricing MI Vendor Error',
      emailBody: 'MI in Scenario Pricing results outside of a Lead/Loan failed to load correctly due to error encountered with the vendor',
    },
    standard: {
      detail: 'Some information may be missing. Re-run MI or contact support to resolve the issue.',
      emailSubject: 'MI Pricing Results Failed to Load Correctly',
      emailBody: 'MI in Scenario Pricing results outside of a Lead/Loan failed to load correctly.',
    },
  },
  [HttpErrorSource.ProductGroupAndDetails]: {
    vendor: {
      detail:
        'The system encountered an error from the <span class="fw-semibold">vendor</span>. Re-run pricing or contact support to resolve the issue.',
      emailSubject: 'Scenario Pricing Vendor Error',
      emailBody: 'Scenario Pricing results outside of a Lead/Loan failed to load correctly due to error encountered with the vendor.',
    },
    standard: {
      detail: 'Some information may be missing. Re-run pricing or contact support to resolve the issue.',
      emailSubject: 'Scenario Pricing Results Failed to Load Correctly.',
      emailBody: 'Scenario Pricing results outside of a Lead/Loan failed to load correctly.',
    },
  },
  [HttpErrorSource.ScenarioOptimizer]: {
    vendor: {
      detail: 'Some information may be missing. Re-run Scenario Optimizer or contact support to resolve the issue.',
      emailSubject: 'Scenario Pricing Scenario Optimizer Vendor Error',
      emailBody:
        'Scenario Optimizer in Scenario Pricing results outside of a Lead/Loan failed to load correctly due to error encountered with the vendor.',
    },
    standard: {
      detail: 'Some information may be missing. Re-run Scenario Optimizer or contact support to resolve the issue.',
      emailSubject: 'Scenario Optimizer Results Failed to Load Correctly',
      emailBody: 'Scenario Optimizer in Scenario Pricing results outside of a Lead/Loan failed to load correctly.',
    },
  },
  default: {
    vendor: {
      detail: 'The system encountered an error from the <span class="fw-semibold">vendor</span>. Try again or contact support to resolve the issue.',
      emailSubject: 'Scenario Pricing Vendor Error for Full Rate Sheet',
      emailBody: 'Scenario Pricing results outside of a Lead/Loan failed to load full rate sheet due to error encountered with the vendor.',
    },
    standard: {
      detail: 'The system encountered an error. Try again or contact support to resolve the issue.',
      emailSubject: 'Saving Scenario Pricing Results Failed',
      emailBody: 'Saving Scenario Pricing results outside of a Lead/Loan failed.',
    },
  },
};

export const enum MiModalTab {
  Monthly = 0,
  Split = 1,
  Upfront = 2,
  LPMI = 3,
  SingleCompany = 4,
}

export const MiModalSelectedTabMap: Record<MiModalTab, PmiType> = {
  [MiModalTab.Monthly]: PmiType.BorrowerMonthlyNonRefundableConstant,
  [MiModalTab.Split]: PmiType.SplitConstantUpfront100,
  [MiModalTab.Upfront]: PmiType.BorrowerSingleNonRefundable,
  [MiModalTab.LPMI]: PmiType.LenderSingleNonRefundable,
  [MiModalTab.SingleCompany]: null,
};

export enum MiQuoteStatus {
  NoEligibleProducts = 0,
  Success = 1,
  Failure = 2,
  TimeOut = 3,
  Other = 4,
}

export enum MiQuoteResultType {
  Success = 1,
  AllNoEligible = 2,
  AllFailureTimeOutOther = 3,
  AtLeastOneSuccess = 4,
}

export interface MiQuoteViewModelConfig {
  status: MiQuoteResultType | number;
  value: number | string;
  isRerun: boolean;
}

export interface MiQuoteViewModel {
  [key: string]: MiQuoteViewModelConfig;
}
export interface PipelineScenarioFilter {
  columnName: string;
  filterValue: string | Date;
  operator: PipelineScenarioFilterOperator;
  condition?: 'or' | 'and';
}

export interface PipelineScenarioSort {
  columnName: string;
  isDescending?: boolean;
}

export interface PipelineScenarioSearchCriteria {
  filters: PipelineScenarioFilter[];
  sort: PipelineScenarioSort[];
  /**
   * offsetN is the number of records to skip
   * offsetN = pageSize * pageIndex0Based
   * pageIndex0Based = 0; pageSize = 10; offSetN = 0
   */
  offsetN?: number;
  topN?: number;
  globalSearchQuery?: string;
  isLayoutNeeded?: boolean;
  activeView?: ViewIcon | string;
}

export type ScenarioLayoutColumnType = 'Text' | 'Date' | 'Phone' | 'Email' | 'Percent' | 'Checkbox' | 'Currency' | 'Enhanced';

export type filterMatchMode =
  | 'startsWith'
  | 'contains'
  | 'notContains'
  | 'endsWith'
  | 'equals'
  | 'notEquals'
  | 'blank'
  | 'notBlank'
  | 'dateIs'
  | 'dateIsNot'
  | 'dateBefore'
  | 'dateOnOrBefore'
  | 'dateAfter'
  | 'dateOnOrAfter'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'gt';

export interface ScenarioFilterMetadata extends FilterMetadata {
  field: string;
  value: string | null;
  matchMode: filterMatchMode;
  operator?: 'and' | 'or';
  isDeleted?: boolean;
  fieldType?: ScenarioFieldType;
}

export class ScenarioLayout {
  guid?: string;
  name?: string;
  displayOrder?: number;
  globalTextFilter?: string;
  pageSize?: number;
  userGUID?: string;
  columns?: ColumnData[];
  viewType?: 'Card' | 'List';
}

export interface RunBestXMiRequest {
  loanPurpose?: LoanPurpose | string | number;
  loanTerms?: LoanTerm[] | number[] | string[];
  armFixedTerm?: number;
  armFirstCap?: number;
  armMaxIncreaseCap?: number;
  armPeriodicCap?: number;
  armSubsequentChangePeriod?: ArmSubsequentChangePeriod | number | string;
  purchasePrice?: number;
  estimatedValue?: number;
  baseLoanAmount?: number;
  fico?: number;
  dti?: number;
  street?: string;
  city?: string;
  state?: string;
  county?: string;
  zip?: string;
  occupancyType?: number | OccupancyType | string;
  propertyType?: number | PropertyType | string;
  mhAdvantage?: boolean;
  noOfUnits?: number | string | NumberOfUnits;
  interestOnly?: boolean;
  firstTimeHomeBuyer?: boolean;
  buyDown?: number | BuyDown | string;
  miProviders?: MIProvider[] | number[];
  htiWithoutMi?: number;
  dtiWithoutMi?: number;
  borrowers?: BorrowerBestXMi[];
  coverageType?: CoverageType | number;
  coveragePercentage?: CoveragePercentage | number;
  scenarioCriteriaGuid?: string;
  rate?: number | string;
  isPud?: boolean;
}

export interface BorrowerBestXMi {
  isPrimary?: boolean;
  fico?: number;
  selfEmployed?: boolean;
  hadForeclosure?: boolean;
  hadBankruptcy?: boolean;
}

export interface RunBestXMiResponse {
  results?: RunBestXMiResult[];
  scenarioCriteriaGuid?: string;
}

export interface RunBestXMiResult {
  armFixedTerm?: number;
  loanTerm?: number;
  bestExResponses?: BestExResponse[];
  disclosureStatements?: string[];
  miSearchId?: string;
  successful?: boolean;
}

export interface BestExResponse {
  pmiType?: number;
  miQuotes?: MiQuote[];
}

export interface MiQuote {
  provider?: number | MIProviderWithoutBestEx;
  rank?: number;
  status?: number;
  premiumUpfrontAmount?: number;
  premiumUpfrontPercent?: number;
  firstMonthlyPremiumAmount?: number;
  firstMonthlyPremiumRate?: number;
  premiumPaidBy?: number;
  premiumRefundable?: boolean;
  calculationType?: number;
  coveragePercent?: number;
  quoteId?: string;
}

export interface BestExResultVM {
  bestExResponses?: BestExResponse[];
  successful?: boolean;
}

export interface ColumnData {
  columnId?: string;
  /**
   * fieldId is only for BE purposes and not FE
   */
  fieldId?: string;
  displayOrder?: number;
  columnWidth?: number;
  sortDirection?: SortDirection;
  sortPriority?: number;
  filters?: ScenarioFilterMetadata[];
  isPinned?: boolean;
  isGroupedBy?: boolean;
}

export type SortDirection = 'Ascending' | 'Descending';

export const fractionDigitsConfigCurrencyFields: Record<string, number> = {
  purchasePrice: 0,
  estimatedValue: 0,
  baseLoanAmount: 0,
  downPaymentAmount: 0,
  heLoanAmount: 0,
  helocDrawnAmount: 0,
  helocLineAmount: 0,
  cashOutAmount: 0,
};

export const fractionDigitsConfigPercentFields: Record<string, number> = {
  baseLtv: 2,
  dti: 2,
  armFirstCap: 0,
  armPeriodicCap: 0,
  armRelativeCap: 0,
  coveragePercent: 0,
  htiWithoutMi: 2,
  dtiWithoutMi: 2,
  monthlyPremiumRate: 2,
};

export const fractionDigitsConfigNumberFields: Record<string, number> = {
  price: 3,
};

// TODO: replace vmScenariosTable.globalFilterQuery
export const operatorLookup: Record<string, PipelineScenarioFilterOperator> = {
  ['in']: PipelineScenarioFilterOperator.InList,
  ['equals']: PipelineScenarioFilterOperator.Equals,
  ['notEquals']: PipelineScenarioFilterOperator.NotEquals,
  ['lt']: PipelineScenarioFilterOperator.LessThan,
  ['lte']: PipelineScenarioFilterOperator.LessThanOrEqual,
  ['gt']: PipelineScenarioFilterOperator.GreaterThan,
  ['gte']: PipelineScenarioFilterOperator.GreaterThanOrEqual,
  ['blank']: PipelineScenarioFilterOperator.Blank,
  ['notBlank']: PipelineScenarioFilterOperator.NotBlank,
  ['startsWith']: PipelineScenarioFilterOperator.StartsWith,
  ['endsWith']: PipelineScenarioFilterOperator.EndsWith,
  ['contains']: PipelineScenarioFilterOperator.Contains,
  ['notContains']: PipelineScenarioFilterOperator.NotContains,
  ['dateIs']: PipelineScenarioFilterOperator.DateIs,
  ['dateIsNot']: PipelineScenarioFilterOperator.DateIsNot,
  ['dateBefore']: PipelineScenarioFilterOperator.DateBefore,
  ['dateOnOrBefore']: PipelineScenarioFilterOperator.DateOnOrBefore,
  ['dateAfter']: PipelineScenarioFilterOperator.DateAfter,
  ['dateOnOrAfter']: PipelineScenarioFilterOperator.DateOnOrAfter,
};

export enum TableTab {
  All = 1,
  BestPricing = 2,
  CompareProducts = 3,
}

export const tableTabValues: SelectItem<TableTab>[] = [
  { value: TableTab.All, label: 'All' },
  { value: TableTab.BestPricing, label: 'Best Pricing' },
  { value: TableTab.CompareProducts, label: 'Compare Products' },
];

export enum PaymentTabs {
  PAndI = 1,
  PITI = 2,
}

export const paymentTabValues: SelectItem<PaymentTabs>[] = [
  { value: PaymentTabs.PAndI, label: 'P&I' },
  { value: PaymentTabs.PITI, label: 'PITI' },
];

export interface GovernmentMiFactor {
  monthlyPmiMipFfGFeePercentGovt: number;
  monthlyPmiMipFfGFeeAmountGovt: number;
  upfrontPmiMipFfGFeePercentGovt: number;
  upfrontPmiMipFfGFeeAmountGovt: number;
  upfrontPmiMipFfGFeeAmountFinancedGovt: number;
  upfrontPmiMipFfGFeeAmountCashGovt: number;
  totalLoanAmountGovt: number;
  loanTerm: LoanTerm | number;
}

export enum ViewIcon {
  List = 1,
  Card = 2,
}

export const viewIconMap: Record<ViewIcon, string> = {
  [ViewIcon.List]: 'List',
  [ViewIcon.Card]: 'Card',
};

export interface PricingScenarioTab {
  id: string;
  title?: string;
  subtitle: string;
}

export enum AddScenarioPricingTabFrom {
  SavedScenario = 'SavedScenario',
  NewScenario = 'NewScenario',
  BestXMI = 'BestXMI',
  ScenarioOptimizer = 'ScenarioOptimizer',
  Duplicate = 'Duplicate',
}

export const scenarioPricingTabSubTitleMap: Record<AddScenarioPricingTabFrom, string> = {
  [AddScenarioPricingTabFrom.BestXMI]: 'BestX MI',
  [AddScenarioPricingTabFrom.ScenarioOptimizer]: 'Scenario Optimizer',
  [AddScenarioPricingTabFrom.SavedScenario]: 'You',
  [AddScenarioPricingTabFrom.NewScenario]: 'You',
  [AddScenarioPricingTabFrom.Duplicate]: 'You',
};

export interface PricingScenario {
  tabId: string;
  from: AddScenarioPricingTabFrom;
  initialFormValue: PricingInputFormModel;
  currentFormValue?: PricingInputFormModel;
  onRunPricingDTO?: PricingInputFormModel;
  pricingRunData?: PricingRunData;
  bestPricing?: Record<number, BestPricing>;
  scenarioName?: string;
  optimizerSuggestion?: Suggestion;
  rerunPricingTitleInfo?: RerunPricingTitleInfo;
  rerunPricingFromBestXmiHasBeenDone?: boolean;
  sortConfig?: Record<TableTab, SortConfig>;
  accordionState?: number[];
  activeOperationsCount?: number;
}

export interface AddNewScenarioTabOptions {
  savedScenarioCriteria?: ScenarioCriteriaModel;
  savedScenario?: PricingResult;
  initialFormValue?: PricingInputFormModel;
  titleObjForRerunPricing?: RerunPricingTitleInfo;
  duplicatedTab?: PricingScenarioTab;
  suggestion?: Suggestion;
}

export interface SortConfig {
  sortField: string;
  sortOrder: number;
}
